import { AssignmentOutlined, CodeOutlined } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import { grey, yellow } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import isObject from "lodash/isObject";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { agApiAutoSizeColumns } from "../../_src_shared/utils/agGrid/api/agApiAutoSizeColumns";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import CellRendererDownloadCreditAuthPdf from "../../components/agGrid/cellRenderer/CellRendererDownloadCreditAuthPdf";
import CellRendererLoanFiles from "../../components/agGrid/cellRenderer/CellRendererLoanFiles";
import CellRendererPullCreditReportButton from "../../components/agGrid/cellRenderer/CellRendererPullCreditReportButton";
import ApplicationAssignmentModal from "../../components/common/ApplicationAssignmentModal";
import Flx from "../../components/layout/Flx";
import JsonPreviewModal from "../../components/modals/JsonPreviewModal";
import MoreOptionsDropdown from "../../components/ui/menus/MoreOptionsDropdown";
import { sideBarColumnsFilters } from "../../utils/agGrid/sideBar/sideBarColumnsFilters";
import { useEnv } from "../../hooks/useEnv";
import { Box } from "@mui/material";
import Txt from "../../components/typography/Txt";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatDateShort } from "../../utils/dates/formatDateShort";
import CellRendererAppLink from "../../components/agGrid/cellRenderer/CellRendererAppLink";

const modalTypesEnums = {
  ASSIGN: "assign",
  PREVIEW: "preview",
  FORM_SPY: "formSpy",
  RUN_REPORT: "runReport",
};

const CreditAuthsTable = ({ quickFilter, rowData }) => {
  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const { isResilender } = useEnv();
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Actions",
        pinned: "left",
        width: 90,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          paddingLeft: "6px",
        },
        cellRenderer: (params) => {
          return (
            <Flx ac>
              <CellRenderRowActionsDropdown params={params} />
              <CellRendererDownloadCreditAuthPdf {...params} />
            </Flx>
          );
        },
      },

      {
        headerName: "Credit Report",
        pinned: "left",
        width: 135,
        hide: !isResilender,
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
        cellRenderer: CellRendererPullCreditReportButton,
      },
      {
        headerName: "Assigned Loan",
        field: "dealId",
        pinned: "left",
        valueFormatter: (p) => {
          if (isNil(p.value)) {
            return "Requires Loan";
          }
          const loanId = p.value;
          if (isNil(loanPipeline)) {
            return loanId;
          }
          const loanNumber = loanPipeline?.find(
            (d) => d?._id === loanId
          )?.loanNumber;
          return loanNumber;
        },
        cellStyle: (p) => {
          if (isNil(p.value)) {
            return {
              fontSize: "13px",
              background: yellow[50],
              color: grey[500],
            };
          }
        },
      },
      {
        headerName: "Assigned Salesperson",
        field: "salesperson",
        pinned: "left",
        valueFormatter: (p) => {
          if (isNil(p.value)) {
            return "Requires Salesperson";
          }
          if (isObject(p.value)) {
            return p.value?.fullName;
          }

          return p?.value;
        },
        cellStyle: (p) => {
          if (isNil(p.value)) {
            return {
              fontSize: "13px",
              background: yellow[50],
              color: grey[500],
            };
          }
        },
      },
      {
        headerName: "Submitted",
        type: "date",
        width: 130,
        sort: "desc",
        field: "submission_date",
      },

      {
        headerName: "App Root Loan Number",
        hide: true,
        field: "loanNumber",
      },
      {
        headerName: "Assigned By",
        hide: true,
        field: "assigned_by",
        pinned: "left",
        valueFormatter: (p) => {
          if (isNil(p.value)) {
            return "Click to assign";
          }
          if (isObject(p.value)) {
            return p.value?.fullName;
          }

          return p?.value;
        },
      },

      { field: "firstName", headerName: "First Name", hide: true },
      { field: "lastName", headerName: "Last Name", hide: true },
      {
        headerName: "Borrower Name",
        valueGetter: (params) => {
          return `${params.data.firstName} ${params.data.lastName}`;
        },
      },
      {
        field: "documents",
        headerName: "Uploaded Files",
        cellRenderer: CellRendererLoanFiles,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          padding: "0 8px",
        },
      },
      {
        headerName: "Sent Application Link",
        field: "app_link",
        width: 350,
        flexGrow: 1,
        autoHeight: true,
        cellRenderer: (p) => (
          <CellRendererAppLink params={p} loanPipeline={loanPipeline} />
        ),
      },
      {
        field: "phone",
        headerName: "Phone",
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          const phone = params.value;
          if (!phone) return "";
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
        },
      },
      { field: "emailAddress", headerName: "Email" },
      { field: "citizenshipStatus", headerName: "Citizenship Status" },
      {
        field: "dateOfBirth",
        headerName: "Date of Birth",
        valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
      },
      {
        field: "borrowerHasSSN",
        headerName: "Has SSN",
        valueFormatter: (params) => (params.value ? "Yes" : "No"),
      },
      { field: "borrowerSSN", headerName: "SSN" },
      { field: "maritalStatus", headerName: "Marital Status" },
      { field: "primaryAddress.fullAddress", headerName: "Full Address" },
      { field: "primaryAddress.streetNumber", headerName: "Street Number" },
      { field: "primaryAddress.streetName", headerName: "Street Name" },
      { field: "primaryAddress.zip", headerName: "ZIP Code" },
      { field: "primaryAddress.county", headerName: "County" },
      { field: "primaryAddress.state", headerName: "State" },
      { field: "primaryAddress.city", headerName: "City" },
      { field: "primaryAddress.country", headerName: "Country" },
      { field: "primaryAddress.latitude", headerName: "Latitude" },
      { field: "primaryAddress.longitude", headerName: "Longitude" },
      {
        field: "signature",
        headerName: "Signature",
        cellRenderer: (params) =>
          `<img src="${params.value}" width="100" height="50" />`,
      },
    ];
  }, [loanPipeline, rowData, isResilender]);

  const defaultColDef = useMemo(() => {
    return {
      enableRowGroup: true,
      suppressHeaderFilterButton: true,
      suppressFloatingFilterButton: true,
      showRowGroup: false,
      cellClass: "ag-cell-vertical-center",
    };
  }, []);

  const columnTypes = useColumnTypes();

  const sideBar = useMemo(() => sideBarColumnsFilters(), []);

  const onFirstDataRendered = useCallback((params) => {
    agApiAutoSizeColumns(params);
  }, []);

  if (isNil(rowData)) {
    return (
      <Flx center sx={{ height: "100%" }} fw g={1}>
        <CircularProgress />
      </Flx>
    );
  }

  return (
    <AgGridTableWrapper height={"100%"} noBorder>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        quickFilterText={quickFilter}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data._id}
        groupDisplayType={"groupRows"}
        columnTypes={columnTypes}
        sideBar={sideBar}
        onFirstDataRendered={onFirstDataRendered}
      />
    </AgGridTableWrapper>
  );
};

const CellRenderRowActionsDropdown = ({ params }) => {
  const [modal, setModal] = useState(false);
  const applicationData = useMemo(() => params?.data, [params?.data]);

  const onClear = useCallback((props) => {
    setModal(false);
  }, []);

  const items = useMemo(
    () => [
      {
        label: "Assign Application",
        icon: <AssignmentOutlined className="thin" fontSize="small" />,
        onClick: () => setModal(modalTypesEnums.ASSIGN),
      },

      {
        label: "View Raw App Data",
        icon: <CodeOutlined className="thin" fontSize="small" />,

        onClick: () => setModal(modalTypesEnums.PREVIEW),
      },
    ],
    [setModal]
  );

  return (
    <>
      <MoreOptionsDropdown
        title={"Application Actions"}
        tooltip="Application Actions"
        items={items}
      />

      {modal === modalTypesEnums.ASSIGN ? (
        <ApplicationAssignmentModal
          application={
            modal === modalTypesEnums.ASSIGN ? applicationData : null
          }
          type={"creditAuths"}
          onClearSelected={onClear}
        />
      ) : null}

      <JsonPreviewModal
        title="Credit Authorization Preview"
        values={modal === modalTypesEnums.PREVIEW ? applicationData : null}
        show={modal === modalTypesEnums.PREVIEW}
        onClose={onClear}
        defaultCollapse={1}
      />
    </>
  );
};

export default CreditAuthsTable;
